import React from "react";
import PropTypes from "prop-types";
import { SEVERITY } from "ppt/constants";
import { getSeverityColor } from "ppt/utilities";
import Badge from "@dtci/spellbook/Badge";

const stagePropType = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  position: PropTypes.number,
  state: PropTypes.string,
});

/**
 * Renders the current stage as a colored chip on the promo list view.
 *
 * `value` is the backend-derived `currentStage` — i.e., the first stage whose
 * state is ACTIVE. It is null only when every stage on the promo has reached
 * COMPLETE: the terminal Done state. The renderer relies on `data.stages` (the
 * full per-row stages array, also returned by the list endpoint) to confirm
 * that interpretation.
 *
 *   - non-null `value`              → render the stage's name in its state color.
 *   - null `value` + all COMPLETE   → render an explicit "Done" indicator.
 *   - null `value` + some non-COMPLETE → render a distinct "Stage unavailable"
 *     warning. This is unexpected (spec 004's invariant says non-terminal
 *     promos must have exactly one ACTIVE) and indicates a regression of the
 *     transition trigger graph; we keep it visibly distinct so a future bug
 *     cannot silently re-mask itself behind a friendly label.
 */
const PPTStatusCellRenderer = ({ value, data }) => {
  const severityColor = getSeverityColor(data?.maxSeverity);

  if (!value) {
    const stages = data?.stages ?? [];

    // Find the last stage by position
    const lastStage = stages.reduce((max, stage) =>
      !max || (stage?.position ?? 0) > (max.position ?? 0) ? stage : max
    , null);

    // If the last stage is complete, show "Done"
    if (lastStage && lastStage.state === "complete") {
      return (
        <Badge light={false} color="success" data-testid="ppt-stage-done">
          Done
        </Badge>
      );
    }

    return (
      <Badge light color={severityColor} data-testid="ppt-stage-unavailable">
        Stage unavailable
      </Badge>
    );
  }

  const state = value.state;
  const light = state !== "complete";

  return (
    <Badge light={light} color={severityColor}>
      {value.name}
    </Badge>
  );
};

PPTStatusCellRenderer.propTypes = {
  value: stagePropType,
  data: PropTypes.shape({
    stages: PropTypes.arrayOf(stagePropType),
  }),
};

export default PPTStatusCellRenderer;
